import React, { useCallback, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  KeyboardSensor,
  useSensor,
  useSensors,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import styled from 'styled-components'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
  GroupPayload,
  TaskPayload,
  tasksGroupReordered,
  tasksReordered,
  taskMovedToGroup,
} from './tasks-slice'
import { DragStateContext, DragState } from './dragContext'

import TaskGroup from './TaskGroup'

const TaskDragPreview = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  background-color: var(--sn-stylekit-background-color);
  border: 1px solid var(--sn-stylekit-border-color);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  color: var(--sn-stylekit-foreground-color);
  font: inherit;
  pointer-events: none;
  max-width: 80vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

function findActive(
  groups: GroupPayload[],
  activeId: string
):
  | { kind: 'group'; group: GroupPayload }
  | { kind: 'task'; task: TaskPayload; groupName: string }
  | null {
  const group = groups.find((g) => g.name === activeId)
  if (group) return { kind: 'group', group }
  for (const g of groups) {
    const t = g.tasks.find((t) => t.id === activeId)
    if (t) return { kind: 'task', task: t, groupName: g.name }
  }
  return null
}

const TaskGroupList: React.FC = () => {
  const dispatch = useAppDispatch()
  const groupedTasks = useAppSelector((state) => state.tasks.groups)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overGroupName, setOverGroupName] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const active = activeId ? findActive(groupedTasks, activeId) : null

  const dragState: DragState = {
    kind: active ? active.kind : null,
    sourceGroupName:
      active?.kind === 'task'
        ? active.groupName
        : active?.kind === 'group'
        ? active.group.name
        : null,
    overGroupName,
  }

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const activeId = String(args.active.id)
      const isGroupDrag = groupedTasks.some((g) => g.name === activeId)

      if (isGroupDrag) {
        // Restrict collisions to other group sortables.
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) =>
            groupedTasks.some((g) => g.name === String(c.id))
          ),
        })
      }

      // Task drag.
      const sourceGroup = groupedTasks.find((g) =>
        g.tasks.some((t) => t.id === activeId)
      )
      const pointer = args.pointerCoordinates

      // Within source group → collide against sibling tasks for live reorder.
      if (sourceGroup && pointer) {
        const sourceContainer = args.droppableContainers.find(
          (c) => String(c.id) === sourceGroup.name
        )
        const r = sourceContainer?.rect.current
        const inSource =
          !!r &&
          pointer.x >= r.left &&
          pointer.x <= r.right &&
          pointer.y >= r.top &&
          pointer.y <= r.bottom

        if (inSource) {
          return closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter((c) =>
              sourceGroup.tasks.some((t) => t.id === String(c.id))
            ),
          })
        }
      }

      // Outside source group → collide against group containers (excluding source).
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (c) =>
            groupedTasks.some((g) => g.name === String(c.id)) &&
            String(c.id) !== sourceGroup?.name
        ),
      })
    },
    [groupedTasks]
  )

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id))
    setOverGroupName(null)
  }

  function handleDragOver(event: DragOverEvent) {
    const { over, active } = event
    if (!over) {
      setOverGroupName(null)
      return
    }
    const overId = String(over.id)
    const activeIdStr = String(active.id)
    // If the active is a task and over is a group name → highlight that group.
    const isTaskDrag = !groupedTasks.some((g) => g.name === activeIdStr)
    if (isTaskDrag && groupedTasks.some((g) => g.name === overId)) {
      setOverGroupName(overId)
    } else {
      setOverGroupName(null)
    }
  }

  function handleDragCancel() {
    setActiveId(null)
    setOverGroupName(null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    setOverGroupName(null)
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const activeId = String(active.id)
    const overId = String(over.id)

    // 1. Group reorder
    const activeGroupIndex = groupedTasks.findIndex((g) => g.name === activeId)
    if (activeGroupIndex !== -1) {
      const overGroupIndex = groupedTasks.findIndex((g) => g.name === overId)
      if (overGroupIndex !== -1) {
        dispatch(
          tasksGroupReordered({
            swapGroupIndex: activeGroupIndex,
            withGroupIndex: overGroupIndex,
          })
        )
      }
      return
    }

    // 2. Task drag — find source group
    let sourceGroupName: string | null = null
    let sourceTaskIndex = -1
    for (const g of groupedTasks) {
      const i = g.tasks.findIndex((t) => t.id === activeId)
      if (i !== -1) {
        sourceGroupName = g.name
        sourceTaskIndex = i
        break
      }
    }
    if (!sourceGroupName) return

    const sourceGroup = groupedTasks.find((g) => g.name === sourceGroupName)!
    const activeTask = sourceGroup.tasks[sourceTaskIndex]

    // 2a. Drop target is another group container → cross-group move.
    const destGroupIndex = groupedTasks.findIndex((g) => g.name === overId)
    if (destGroupIndex !== -1 && overId !== sourceGroupName) {
      if (activeTask.completed) return
      dispatch(
        taskMovedToGroup({
          taskId: activeId,
          fromGroup: sourceGroupName,
          toGroup: overId,
        })
      )
      return
    }

    // 2b. Drop target is a sibling task in the source group → reorder.
    const overTaskIndex = sourceGroup.tasks.findIndex((t) => t.id === overId)
    if (overTaskIndex === -1 || overTaskIndex === sourceTaskIndex) return
    const overTask = sourceGroup.tasks[overTaskIndex]
    const isSameSection = !!activeTask.completed === !!overTask.completed
    dispatch(
      tasksReordered({
        groupName: sourceGroupName,
        swapTaskIndex: sourceTaskIndex,
        withTaskIndex: overTaskIndex,
        isSameSection,
      })
    )
  }

  const groupIds = groupedTasks.map((g) => g.name)

  return (
    <div data-testid="task-group-list">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <DragStateContext.Provider value={dragState}>
          <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
            {groupedTasks.map((group, index) => (
              <TaskGroup
                key={`group-${group.name}`}
                group={group}
                isDragging={false}
                isLast={groupedTasks.length - 1 === index}
              />
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {active?.kind === 'task' && (
              <TaskDragPreview>{active.task.description}</TaskDragPreview>
            )}
          </DragOverlay>
        </DragStateContext.Provider>
      </DndContext>
    </div>
  )
}

export default TaskGroupList
